import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser, SignOutButton } from '@clerk/clerk-react';

const AdminNavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${props => props.theme.zIndex.fixed};
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const AdminNavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.25rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: clamp(1rem, 2.5vw, 2.5rem);
  min-width: 0; /* Ermöglicht Shrinking */
  
  @media (max-width: 1200px) {
    gap: clamp(0.75rem, 2vw, 2rem);
    padding: 1.25rem 1.75rem;
  }
  
  @media (max-width: 1100px) {
    gap: clamp(0.5rem, 1.5vw, 1.5rem);
    padding: 1.25rem 1.5rem;
  }
  
  @media (max-width: 1000px) {
    gap: clamp(0.5rem, 1vw, 1rem);
    padding: 1rem 1.25rem;
  }
  
  @media (max-width: 900px) {
    gap: 0.75rem;
    padding: 1rem 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const AdminLogo = styled.div`
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  padding: 0.5rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  flex-shrink: 0; /* Logo darf nicht schrumpfen */
  min-width: 0; /* Ermöglicht Text-Overflow */
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(102, 126, 234, 0.1);
  }
  
  @media (max-width: 1000px) {
    padding: 0.4rem;
    gap: 0.6rem;
  }
  
  @media (max-width: 900px) {
    padding: 0.35rem;
    gap: 0.5rem;
  }
`;

const AdminLogoImage = styled.img`
  height: clamp(2rem, 3vw, 2.5rem);
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    height: 2rem;
  }
`;

const AdminLogoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AdminLogoText = styled.h2`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(1.4rem, 2.5vw, 2rem);
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 1000px) {
    font-size: clamp(1.3rem, 2vw, 1.8rem);
  }
  
  @media (max-width: 900px) {
    font-size: clamp(1.2rem, 1.8vw, 1.6rem);
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const AdminLogoSubtitle = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textLight};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: -0.25rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const AdminNavMenu = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: clamp(0.35rem, 0.5vw, 0.5rem);
  gap: clamp(0.3rem, 0.4vw, 0.5rem);
  background: rgba(102, 126, 234, 0.08);
  border-radius: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(102, 126, 234, 0.2);
  flex-shrink: 1; /* NavMenu kann schrumpfen */
  min-width: 0; /* Ermöglicht Shrinking */
  overflow-x: auto; /* Fallback für sehr kleine Fenster */
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox - Scrollbar verstecken */
  -ms-overflow-style: none; /* IE/Edge - Scrollbar verstecken */
  
  /* Chrome/Safari - Scrollbar verstecken */
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 1100px) {
    padding: clamp(0.3rem, 0.4vw, 0.45rem);
    gap: clamp(0.25rem, 0.35vw, 0.4rem);
  }
  
  @media (max-width: 1000px) {
    padding: clamp(0.25rem, 0.3vw, 0.4rem);
    gap: clamp(0.2rem, 0.3vw, 0.35rem);
  }
  
  @media (max-width: 900px) {
    padding: 0.3rem;
    gap: 0.25rem;
  }
  
  /* Mobile Menu Styles - nur bei 768px und kleiner */
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 320px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.95));
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 7rem 2rem 2rem;
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.15);
    transform: translateX(${props => props.isOpen ? '0' : '100%'});
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    gap: 0.5rem;
    border: none;
    border-left: 3px solid #667eea;
    overflow-x: visible;
    overflow-y: visible;
  }
  
  /* Verstecke Desktop-Menü zwischen 900px und 769px */
  @media (min-width: 769px) and (max-width: 900px) {
    display: none !important;
  }
`;

const AdminNavLink = styled.button`
  text-decoration: none;
  font-weight: 500;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  color: ${props => props.$isMobile ? props.theme.colors.text : props.theme.colors.text};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  padding: clamp(0.6rem, 0.8vw, 0.75rem) clamp(1rem, 1.5vw, 1.5rem);
  border-radius: 20px;
  display: flex;
  align-items: center;
  letter-spacing: 0.3px;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap; /* Verhindert Textumbruch */
  flex-shrink: 0; /* Links schrumpfen nicht, sondern werden scrollbar */
  min-width: fit-content;
  
  &:hover {
    color: #667eea;
    background: rgba(102, 126, 234, 0.15);
    transform: translateY(-1px);
  }
  
  &.active {
    color: white;
    background: linear-gradient(45deg, #667eea, #764ba2);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    transform: translateY(-2px);
  }
  
  @media (max-width: 1100px) {
    font-size: clamp(0.75rem, 0.9vw, 0.9rem);
    padding: clamp(0.55rem, 0.7vw, 0.7rem) clamp(0.9rem, 1.3vw, 1.3rem);
  }
  
  @media (max-width: 1000px) {
    font-size: clamp(0.7rem, 0.85vw, 0.85rem);
    padding: clamp(0.5rem, 0.65vw, 0.65rem) clamp(0.8rem, 1.2vw, 1.2rem);
  }
  
  @media (max-width: 900px) {
    font-size: clamp(0.65rem, 0.8vw, 0.8rem);
    padding: clamp(0.45rem, 0.6vw, 0.6rem) clamp(0.7rem, 1vw, 1rem);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    border-radius: 15px;
    width: 100%;
    background: ${props => props.className?.includes('active') 
      ? 'linear-gradient(45deg, #667eea, #764ba2)' 
      : 'rgba(255, 255, 255, 0.7)'};
    color: ${props => props.className?.includes('active') ? 'white' : props.theme.colors.text};
    border: 1px solid ${props => props.className?.includes('active') 
      ? 'transparent' 
      : 'rgba(102, 126, 234, 0.2)'};
    white-space: normal;
    flex-shrink: 1;
    
    &:hover {
      background: ${props => props.className?.includes('active') 
        ? 'linear-gradient(45deg, #667eea, #764ba2)' 
        : 'rgba(102, 126, 234, 0.15)'};
      transform: translateX(5px);
    }
  }
`;

const AdminMenuToggle = styled.button`
  display: none;
  background: rgba(102, 126, 234, 0.1);
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  cursor: pointer;
  padding: 0.75rem;
  z-index: ${props => props.theme.zIndex.modal};
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  flex-shrink: 0; /* MenuToggle darf nicht schrumpfen */
  
  &:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: scale(1.05);
  }
  
  @media (max-width: 900px) {
    display: block; /* Zeige MenuToggle früher bei 900px */
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const AdminMenuBar = styled.span`
  display: block;
  width: 22px;
  height: 2.5px;
  background: #667eea;
  margin: 4px 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
  border-radius: 2px;
  
  &:nth-child(1) {
    transform: ${props => props.isOpen 
      ? 'rotate(45deg) translate(5px, 5px)' 
      : 'none'};
  }
  
  &:nth-child(2) {
    opacity: ${props => props.isOpen ? '0' : '1'};
    transform: ${props => props.isOpen ? 'scale(0)' : 'scale(1)'};
  }
  
  &:nth-child(3) {
    transform: ${props => props.isOpen 
      ? 'rotate(-45deg) translate(5px, -5px)' 
      : 'none'};
  }
`;

const AdminOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${props => props.theme.zIndex.modal - 1};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const AdminUserSection = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.75rem, 1.5vw, 1.5rem);
  flex-shrink: 0; /* UserSection darf nicht schrumpfen */
  min-width: 0; /* Ermöglicht Shrinking */
  
  @media (max-width: 1000px) {
    gap: clamp(0.5rem, 1vw, 1rem);
  }
  
  @media (max-width: 900px) {
    display: none; /* Verstecke UserSection bei 900px, zeige im Mobile Menu */
  }
`;

const AdminUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.75rem, 1vw, 1rem);
  padding: clamp(0.6rem, 0.75vw, 0.75rem) clamp(1rem, 1.25vw, 1.25rem);
  background: rgba(102, 126, 234, 0.1);
  border-radius: 25px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: clamp(150px, 15vw, 200px);
  flex-shrink: 1; /* UserInfo kann schrumpfen */
  min-width: 0; /* Ermöglicht Shrinking */
  
  &:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.25);
    border-color: rgba(102, 126, 234, 0.3);
  }
  
  @media (max-width: 1100px) {
    max-width: clamp(120px, 12vw, 180px);
  }
  
  @media (max-width: 1000px) {
    max-width: clamp(100px, 10vw, 150px);
  }
`;

const AdminUserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
    z-index: -1;
    opacity: 0.2;
  }
`;

const AdminUserAvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
    z-index: -1;
    opacity: 0.2;
  }
`;

const AdminUserName = styled.span`
  font-size: clamp(0.85rem, 1vw, 1rem);
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: clamp(80px, 10vw, 120px);
  flex-shrink: 1;
  min-width: 0;
  
  &:hover {
    color: #667eea;
  }
  
  &::after {
    content: '⚙️';
    margin-left: 0.5rem;
    font-size: 0.8rem;
    opacity: 0.7;
    transition: opacity 0.3s ease;
    flex-shrink: 0;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  @media (max-width: 1000px) {
    max-width: clamp(60px, 8vw, 100px);
    font-size: clamp(0.75rem, 0.9vw, 0.9rem);
  }
`;

const AdminSignOutBtn = styled.button`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
    background: linear-gradient(135deg, #ff5252 0%, #e53935 100%);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const AdminNavbar = ({ currentView, onViewChange }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useUser();
  
  useEffect(() => {
    // Schließe Mobile Menu bei View-Wechsel
    setMobileMenuOpen(false);
  }, [currentView]);

  const adminNavItems = [
    { view: 'dashboard', label: 'Dashboard', icon: '🏠' },
    { view: 'courses', label: 'Kurse', icon: '🎠' },
    { view: 'bookings', label: 'Buchungen', icon: '📋' },
    { view: 'user-management', label: 'Benutzer', icon: '👥' },
    { view: 'statistics', label: 'Statistiken', icon: '📊' },
    { view: 'user-settings', label: 'Einstellungen', icon: '⚙️' }
  ];

  const getUserInitials = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.emailAddresses?.[0]?.emailAddress) {
      return user.emailAddresses[0].emailAddress[0].toUpperCase();
    }
    return 'A';
  };

  const getUserDisplayName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.emailAddresses?.[0]?.emailAddress) {
      return user.emailAddresses[0].emailAddress;
    }
    return 'Benutzer';
  };

  const getUserEmail = (user) => {
    return user?.emailAddresses?.[0]?.emailAddress || '';
  };

  const handleLogoClick = () => {
    onViewChange('dashboard');
  };

  const handleUserInfoClick = () => {
    onViewChange('user-settings');
  };

  return (
    <AdminNavbarContainer>
      <AdminNavContent>
        <AdminLogo onClick={handleLogoClick}>
          <AdminLogoImage src="/assets/logo.webp" alt="Der Ponystall Logo" />
          <AdminLogoTextContainer>
            <AdminLogoText>DerPonystall</AdminLogoText>
            <AdminLogoSubtitle>Admin Panel</AdminLogoSubtitle>
          </AdminLogoTextContainer>
        </AdminLogo>

        <AdminNavMenu isOpen={mobileMenuOpen}>
          {adminNavItems.map((item, index) => (
            <motion.li 
              key={item.view}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.3, 
                delay: mobileMenuOpen ? index * 0.1 : 0,
                ease: "easeOut"
              }}
            >
              <AdminNavLink 
                onClick={() => {
                  onViewChange(item.view);
                  setMobileMenuOpen(false);
                }}
                className={currentView === item.view ? 'active' : ''}
                $isMobile={window.innerWidth <= 768}
              >
                <span style={{marginRight: '0.5rem'}}>{item.icon}</span> {item.label}
              </AdminNavLink>
            </motion.li>
          ))}
          
          {/* Mobile User Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.3, 
              delay: mobileMenuOpen ? adminNavItems.length * 0.1 : 0,
              ease: "easeOut"
            }}
            style={{ 
              marginTop: '2rem', 
              padding: '1rem', 
              borderTop: '1px solid rgba(102, 126, 234, 0.2)',
              display: window.innerWidth <= 768 ? 'block' : 'none'
            }}
          >
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1.25rem', 
                marginBottom: '1.5rem',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                background: 'rgba(102, 126, 234, 0.05)',
                border: '1px solid rgba(102, 126, 234, 0.1)'
              }}
              onClick={() => {
                handleUserInfoClick();
                setMobileMenuOpen(false);
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(102, 126, 234, 0.15)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(102, 126, 234, 0.05)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {user?.imageUrl ? (
                <AdminUserAvatarImage 
                  src={user.imageUrl} 
                  alt={`${user.firstName} ${user.lastName}`}
                  style={{ width: '50px', height: '50px' }}
                />
              ) : (
                <AdminUserAvatar style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                  {getUserInitials(user)}
                </AdminUserAvatar>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  color: '#2c3e50',
                  marginBottom: '0.25rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {getUserDisplayName(user)}
                </div>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: '#7f8c8d',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {getUserEmail(user)}
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: '#667eea',
                  marginTop: '0.25rem',
                  fontWeight: '500'
                }}>
                  ⚙️ Einstellungen
                </div>
              </div>
            </div>
            <SignOutButton>
              <AdminSignOutBtn style={{ width: '100%' }}>
                Abmelden
              </AdminSignOutBtn>
            </SignOutButton>
          </motion.div>
        </AdminNavMenu>

        {/* Desktop User Section */}
        <AdminUserSection>
          <AdminUserInfo onClick={handleUserInfoClick}>
            {user?.imageUrl ? (
              <AdminUserAvatarImage 
                src={user.imageUrl} 
                alt={`${user.firstName} ${user.lastName}`}
              />
            ) : (
              <AdminUserAvatar>
                {getUserInitials(user)}
              </AdminUserAvatar>
            )}
            <AdminUserName>
              {getUserDisplayName(user)}
            </AdminUserName>
          </AdminUserInfo>
          <SignOutButton>
            <AdminSignOutBtn>Abmelden</AdminSignOutBtn>
          </SignOutButton>
        </AdminUserSection>

        <AdminMenuToggle 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Navigation öffnen/schließen"
        >
          <AdminMenuBar isOpen={mobileMenuOpen} />
          <AdminMenuBar isOpen={mobileMenuOpen} />
          <AdminMenuBar isOpen={mobileMenuOpen} />
        </AdminMenuToggle>
      </AdminNavContent>

      <AnimatePresence>
        {mobileMenuOpen && (
          <AdminOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </AdminNavbarContainer>
  );
};

export default AdminNavbar;